import type { Attribute, AttributeGroup, AttributeValue } from "@/lib/catalog/types";
import { attributeScalarToOptionString } from "@/lib/catalog/attributeScalarString";

export type AttributeVisibilityOperator = "in" | "not_in";

export interface AttributeVisibilityRule {
  operator: AttributeVisibilityOperator;
  parentOptionValues: string[];
}

export function normalizeVisibilityRule(raw: unknown): AttributeVisibilityRule | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const operator = o.operator === "not_in" ? "not_in" : o.operator === "in" ? "in" : undefined;
  if (!operator) return undefined;

  const valuesRaw = o.parent_option_values ?? o.parentOptionValues;
  if (!Array.isArray(valuesRaw)) return undefined;

  const parentOptionValues = valuesRaw.map((v) => String(v).trim()).filter(Boolean);
  if (parentOptionValues.length === 0) return undefined;

  return { operator, parentOptionValues };
}

export function flattenSchemaAttributes(groups: AttributeGroup[]): Attribute[] {
  const out: Attribute[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    for (const attr of group.attributes) {
      if (seen.has(attr.id)) continue;
      seen.add(attr.id);
      out.push(attr);
    }
  }
  return out;
}

export function isAttributeVisible(
  attr: Attribute,
  allAttrs: Attribute[],
  values: Record<string, AttributeValue>,
): boolean {
  if (!attr.parentAttributeId || !attr.visibilityRule) return true;

  const parent = allAttrs.find((a) => a.id === attr.parentAttributeId);
  if (!parent) return false;

  const parentVal = attributeScalarToOptionString(values[parent.code]).trim();
  if (!parentVal) return false;

  const rule = attr.visibilityRule;
  if (rule.operator === "in") {
    return rule.parentOptionValues.includes(parentVal);
  }
  if (rule.operator === "not_in") {
    return !rule.parentOptionValues.includes(parentVal);
  }

  return true;
}

export function filterVisibleAttributes(
  attrs: Attribute[],
  allAttrs: Attribute[],
  values: Record<string, AttributeValue>,
): Attribute[] {
  return attrs.filter((attr) => isAttributeVisible(attr, allAttrs, values));
}

export function collectHiddenAttributeCodes(
  groups: AttributeGroup[],
  values: Record<string, AttributeValue>,
): Set<string> {
  const all = flattenSchemaAttributes(groups);
  const hidden = new Set<string>();
  for (const attr of all) {
    if (!isAttributeVisible(attr, all, values)) {
      hidden.add(attr.code);
    }
  }
  return hidden;
}

export function pruneHiddenAttributeValues(
  values: Record<string, AttributeValue>,
  groups: AttributeGroup[],
): Record<string, AttributeValue> {
  const hidden = collectHiddenAttributeCodes(groups, values);
  if (hidden.size === 0) return values;

  const next = { ...values };
  for (const code of hidden) {
    delete next[code];
  }
  return next;
}

export function validateVisibleRequiredAttributes(
  draft: { attributes: Record<string, AttributeValue> },
  groups: AttributeGroup[],
  onError: (message: string) => void,
  skip?: (attr: Attribute) => boolean,
): boolean {
  const all = flattenSchemaAttributes(groups);
  for (const group of groups) {
    for (const attr of group.attributes) {
      if (skip?.(attr)) continue;
      if (!isAttributeVisible(attr, all, draft.attributes)) continue;
      if (!attr.isRequired) continue;

      const value = draft.attributes[attr.code];
      const missing =
        value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
      if (missing) {
        onError(`Required field: ${attr.name}`);
        return false;
      }
    }
  }
  return true;
}
